import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { ArrowLeft, Printer } from 'lucide-react';
import { EmptyState } from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { bankingKey, type ReconcileLine, type ReconciliationReport } from '@/lib/banking';
import { displayDate } from '@/lib/dates';
import { formatAmount } from '@/lib/format';
import { useOrgId } from '@/lib/org';

export const Route = createFileRoute('/banking/reconcile/$reconciliationId/report')({
  component: ReconciliationReportPage,
});

/** Screen B6 (document 06): reconciliation report, in the report page pattern. */
function ReconciliationReportPage() {
  const orgId = useOrgId();
  const { reconciliationId } = Route.useParams();
  const report = useQuery({
    queryKey: [...bankingKey(orgId), 'reconciliation-report', reconciliationId],
    queryFn: () =>
      api<ReconciliationReport>(
        `/orgs/${orgId}/banking/reconciliations/${reconciliationId}/report`,
      ),
    enabled: Boolean(orgId),
  });
  if (!orgId) return <EmptyState title="Select a client" />;
  if (report.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (report.isError)
    return <EmptyState title="Could not run the report" hint={report.error.message} />;
  const r = report.data;
  const rec = r.reconciliation;
  const c = rec.currency;
  const count = (n: number) => `${n} ${n === 1 ? 'transaction' : 'transactions'}`;

  return (
    <>
      <div className="mb-2 flex items-center justify-between print:hidden">
        <Link
          to="/banking/reconcile"
          search={{ accountId: rec.accountId }}
          className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
        >
          <ArrowLeft className="size-4" /> Back to Reconcile
        </Link>
        <Button variant="ghost" size="icon" aria-label="Print" onClick={() => window.print()}>
          <Printer className="size-4" />
        </Button>
      </div>
      <div className="mx-auto max-w-5xl rounded-lg border bg-card px-8 py-6">
        <header className="mb-6 text-center">
          <div className="text-lg font-semibold">{r.orgName}</div>
          <div className="text-base">
            {rec.accountCode} {rec.accountName}, period ending {displayDate(rec.statementDate)}
          </div>
          <div className="text-sm text-muted-foreground">
            {rec.status === 'finished'
              ? `Reconciled on ${rec.finishedAt ? displayDate(rec.finishedAt.slice(0, 10)) : ''}${rec.finishedByName ? ` by ${rec.finishedByName}` : ''}`
              : rec.status === 'undone'
                ? `Undone on ${rec.undoneAt ? displayDate(rec.undoneAt.slice(0, 10)) : ''}: ${rec.undoReason ?? ''}`
                : 'In progress'}
          </div>
        </header>

        <h2 className="mb-2 font-semibold">Summary</h2>
        <table className="mb-8 w-full text-sm" data-testid="reconciliation-summary">
          <tbody>
            <SummaryRow label="Statement beginning balance" value={rec.beginningBalance} c={c} />
            <SummaryRow
              label={`Cleared transactions – payments (${count(r.clearedPayments.length)})`}
              value={negate(r.totals.clearedPayments)}
              c={c}
              indent
            />
            <SummaryRow
              label={`Cleared transactions – deposits (${count(r.clearedDeposits.length)})`}
              value={r.totals.clearedDeposits}
              c={c}
              indent
            />
            <SummaryRow
              label="Statement ending balance"
              value={rec.statementBalance}
              c={c}
              strong
            />
            <SummaryRow
              label={`Uncleared payments as of ${displayDate(rec.statementDate)} (${count(r.unclearedPayments.length)})`}
              value={negate(r.totals.unclearedPayments)}
              c={c}
              indent
            />
            <SummaryRow
              label={`Uncleared deposits as of ${displayDate(rec.statementDate)} (${count(r.unclearedDeposits.length)})`}
              value={r.totals.unclearedDeposits}
              c={c}
              indent
            />
            <SummaryRow
              label={`Register balance as of ${displayDate(rec.statementDate)}`}
              value={r.totals.registerBalance}
              c={c}
              strong
            />
            <SummaryRow
              label={`New transactions after ${displayDate(rec.statementDate)} (${count(r.unclearedAfter.length)})`}
              value={null}
              c={c}
              indent
            />
            <SummaryRow
              label="Register balance today"
              value={r.totals.registerBalanceToday}
              c={c}
              strong
            />
          </tbody>
        </table>

        <Section title="Cleared payments" lines={r.clearedPayments} side="payment" />
        <Section title="Cleared deposits" lines={r.clearedDeposits} side="deposit" />
        <Section
          title={`Uncleared payments as of ${displayDate(rec.statementDate)}`}
          lines={r.unclearedPayments}
          side="payment"
        />
        <Section
          title={`Uncleared deposits as of ${displayDate(rec.statementDate)}`}
          lines={r.unclearedDeposits}
          side="deposit"
        />
        <Section
          title={`New transactions after ${displayDate(rec.statementDate)}`}
          lines={r.unclearedAfter}
          side="both"
        />
        <footer className="mt-8 text-center text-xs text-muted-foreground">
          {r.orgName} · Reconciliation report · Generated{' '}
          {new Date(r.generatedAt).toLocaleString('en-GB', { hour12: false })}
        </footer>
      </div>
    </>
  );
}

const negate = (v: string) => (v === '0.00' ? v : v.startsWith('-') ? v.slice(1) : `-${v}`);

function SummaryRow({
  label,
  value,
  c,
  indent,
  strong,
}: {
  label: string;
  value: string | null;
  c: string;
  indent?: boolean;
  strong?: boolean;
}) {
  return (
    <tr className={strong ? 'border-t font-semibold' : ''}>
      <td className={`py-1.5 ${indent ? 'pl-6' : ''}`}>{label}</td>
      <td className="tnum w-[200px] py-1.5 text-right">
        {value === null ? '' : `${c} ${formatAmount(value)}`}
      </td>
    </tr>
  );
}

function Section({
  title,
  lines,
  side,
}: {
  title: string;
  lines: ReconcileLine[];
  side: 'payment' | 'deposit' | 'both';
}) {
  if (!lines.length) return null;
  return (
    <section className="mb-6">
      <h3 className="mb-1 text-sm font-semibold">{title}</h3>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-xs uppercase tracking-wide text-muted-foreground">
            <th className="w-[110px] py-1.5 text-left font-medium">Date</th>
            <th className="w-[140px] py-1.5 text-left font-medium">Type</th>
            <th className="w-[80px] py-1.5 text-left font-medium">Ref no.</th>
            <th className="py-1.5 text-left font-medium">Payee</th>
            <th className="py-1.5 text-left font-medium">Memo</th>
            <th className="w-[140px] py-1.5 text-right font-medium">Amount</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.journalLineId} className="border-b border-dashed last:border-0">
              <td className="tnum py-1">{displayDate(l.date)}</td>
              <td className="py-1">{l.type}</td>
              <td className="py-1">
                <Link
                  to="/accounting/journal-entries/$entryId"
                  params={{ entryId: l.entryId }}
                  className="text-primary hover:underline"
                >
                  {l.entryNo}
                </Link>
              </td>
              <td className="py-1">{l.payee ?? ''}</td>
              <td className="max-w-[280px] truncate py-1">{l.memo ?? ''}</td>
              <td className="tnum py-1 text-right">
                {side === 'payment' || (side === 'both' && l.payment)
                  ? formatAmount(`-${l.payment}`)
                  : formatAmount(l.deposit)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
